// ==========================================
// src/components/WeatherCarousel.jsx
// PREVISÃO DO TEMPO REAL — VERSÃO LENDÁRIA
// ✅ Cor branca nos nomes de cidades
// ✅ Gratificação (text-shadow) pra legibilidade
// ✅ Font-weight bold
// ✅ Hover effect interativo
// ==========================================

import React, { useState, useEffect } from 'react';
import { Cloud, CloudRain, Sun, CloudLightning, Wind, MapPin } from 'lucide-react';

const cidades = [
  { nome: 'Mato Grosso', uf: 'MT', lat: -15.6, lon: -56.1 },
  { nome: 'São Paulo', uf: 'SP', lat: -23.5, lon: -46.6 },
  { nome: 'Curitiba', uf: 'PR', lat: -25.4, lon: -49.3 },
  { nome: 'Santos', uf: 'SP', lat: -23.9, lon: -46.3 },
  { nome: 'Porto Alegre', uf: 'RS', lat: -30.0, lon: -51.2 },
];

const iconesTempo = {
  0: <Sun className="w-4 h-4 text-amber-400" />,
  1: <Sun className="w-4 h-4 text-amber-300" />,
  2: <Cloud className="w-4 h-4 text-slate-400" />,
  3: <Cloud className="w-4 h-4 text-slate-500" />,
  45: <Wind className="w-4 h-4 text-slate-400" />,
  48: <Wind className="w-4 h-4 text-slate-400" />,
  51: <CloudRain className="w-4 h-4 text-blue-300" />,
  53: <CloudRain className="w-4 h-4 text-blue-400" />,
  55: <CloudRain className="w-4 h-4 text-blue-500" />,
  61: <CloudRain className="w-4 h-4 text-blue-400" />,
  63: <CloudRain className="w-4 h-4 text-blue-500" />,
  65: <CloudRain className="w-4 h-4 text-blue-600" />,
  80: <CloudRain className="w-4 h-4 text-blue-400" />,
  81: <CloudRain className="w-4 h-4 text-blue-500" />,
  82: <CloudRain className="w-4 h-4 text-blue-600" />,
  95: <CloudLightning className="w-4 h-4 text-purple-400" />,
  96: <CloudLightning className="w-4 h-4 text-purple-500" />,
  99: <CloudLightning className="w-4 h-4 text-purple-600" />,
};

function getIcone(codigo) {
  return iconesTempo[codigo] || <Sun className="w-4 h-4 text-amber-400" />;
}

export default function WeatherCarousel() {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    const buscarTempos = async () => {
      try {
        console.log('🌤️ [WeatherCarousel] Buscando via Edge Function...');
        
        const resultados = await Promise.all(
          cidades.map(async (cidade) => {
            try {
              const response = await fetch(
                'https://zmsvjakcdczgxgjtydwx.supabase.co/functions/v1/weather',
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || ''}`,
                  },
                  body: JSON.stringify({
                    lat: cidade.lat,
                    lon: cidade.lon,
                    cidade: cidade.nome,
                  }),
                }
              );

              if (!response.ok) throw new Error(`HTTP ${response.status}`);

              const data = await response.json();
              
              if (data.temp !== undefined) {
                return {
                  ...cidade,
                  temp: data.temp,
                  weatherCode: data.weather_code,
                };
              }
              throw new Error('Sem temp');
            } catch (err) {
              console.warn(`⚠️ ${cidade.nome}:`, err.message);
              return null;
            }
          })
        );

        const validos = resultados.filter(r => r !== null);
        
        if (validos.length === 0) {
          console.error('❌ Nenhuma cidade retornou dados');
          setErro(true);
        } else {
          console.log('✅ Dados reais:', validos);
          setDados(validos);
        }
      } catch (err) {
        console.error('❌ Erro geral:', err);
        setErro(true);
      } finally {
        setLoading(false);
      }
    };

    buscarTempos();
  }, []);

  // Se deu erro ou não tem dados, NÃO MOSTRA NADA (sem fake)
  if (loading || erro || dados.length === 0) {
    return null;
  }

  const dadosLoop = [...dados, ...dados];

  return (
    <div className="bg-gradient-to-r from-[#1E3A5F] via-[#2a4a6f] to-[#1E3A5F] border-b border-slate-700/50 overflow-hidden relative backdrop-blur-sm">
      {/* Gradientes nas pontas */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#1E3A5F] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#1E3A5F] to-transparent z-10 pointer-events-none" />
      
      <div className="flex animate-[scrollRight_30s_linear_infinite] hover:[animation-play-state:paused]">
        {dadosLoop.map((cidade, index) => (
          <div
            key={`${cidade.uf}-${index}`}
            className="flex items-center gap-2 px-5 py-2 whitespace-nowrap border-r border-white/10 hover:bg-white/10 transition-all duration-300 cursor-default group"
          >
            {/* Ícone do tempo com glow */}
            <div className="flex-shrink-0 opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all">
              {getIcone(cidade.weatherCode)}
            </div>
            
            {/* Nome da cidade — BRANCO COM GRATIFICAÇÃO */}
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-white/60 group-hover:text-white/80 transition-colors" />
              <span 
                className="text-xs font-bold text-white uppercase tracking-wide"
                style={{ 
                  textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 0 10px rgba(255,255,255,0.1)' 
                }}
              >
                {cidade.nome}
              </span>
            </div>
            
            {/* UF */}
            <span 
              className="text-[10px] font-semibold text-white/50 group-hover:text-white/70 transition-colors"
            >
              {cidade.uf}
            </span>
            
            {/* Temperatura */}
            <span 
              className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors"
              style={{ 
                textShadow: '0 1px 2px rgba(0,0,0,0.5)' 
              }}
            >
              {cidade.temp}°
            </span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes scrollRight {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}